import {
  Box,
  Button,
  Grid,
  HStack,
  Icon,
  Image,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { FaExclamationTriangle } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";
import { Link } from "react-router-dom";

export default function CampaignActiveErrorRow({
  campaignId,
  campaignName,
  publishErrors,
  eventDateLabel,
  imageUrl,
  tableGridTemplate,
}: {
  campaignId: string;
  campaignName: string;
  publishErrors: string[];
  eventDateLabel?: string;
  imageUrl?: string;
  tableGridTemplate?: string;
}) {
  const firstError = publishErrors[0] ?? "Unknown publish error";
  const campaignPath = `/campaigns/ads/${campaignId}`;
  const failureMessage =
    publishErrors.length <= 1
      ? `Failed to launch campaign: ${firstError}`
      : `Failed to launch campaign:\n\n${publishErrors.join("\n\n")}`;

  return (
    <Grid
      as={Link}
      to={campaignPath}
      px={4}
      py={3}
      gap={4}
      alignItems="center"
      templateColumns={tableGridTemplate}
      bg="#fff1f1"
      _hover={{ bg: "#f7eaea", textDecoration: "none" }}
    >
      <Box display={"block"} position="relative">
        <Image
          src={imageUrl}
          width="64px"
          height="64px"
          borderRadius="md"
          objectFit="cover"
          alt={campaignName}
        />
        <Box
          position="absolute"
          inset={0}
          borderRadius="md"
          border="1px solid #DDDDDD"
          pointerEvents="none"
        />
      </Box>
      <VStack align="stretch" spacing={0}>
        <HStack align="center" gap={2}>
          <Icon
            as={FaExclamationTriangle}
            color="red.500"
            boxSize={3.5}
            aria-hidden
          />
          <Text
            fontSize="sm"
            fontWeight="600"
            noOfLines={1}
            wordBreak="break-all"
          >
            {campaignName}
          </Text>
        </HStack>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="gray.700"
          noOfLines={1}
        >
          {firstError}
        </Text>
        {eventDateLabel ? (
          <Text
            fontSize="xs"
            fontWeight="medium"
            color="gray.600"
            noOfLines={1}
          >
            {eventDateLabel}
          </Text>
        ) : null}
      </VStack>
      <HStack
        spacing={3}
        justify="flex-start"
        align="center"
        minW={0}
        flexWrap="wrap"
        gridColumn={"3 / span 3"}
        fontFamily="Inter, sans-serif"
      >
        <Tooltip label={failureMessage} hasArrow placement="top" maxW="420px">
          <Text
            fontSize="sm"
            color="gray.900"
            noOfLines={1}
            wordBreak="break-all"
          >
            <Text as="span" fontWeight="semibold">
              Failed to launch campaign:
            </Text>{" "}
            {firstError}
          </Text>
        </Tooltip>
      </HStack>
      <Box
        justifySelf={"flex-end"}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Button
          as={Link}
          to={campaignPath}
          paddingY="10px"
          paddingX="12px"
          fontSize="sm"
          variant="outline"
          bg="white"
          fontWeight="medium"
          rightIcon={<Icon as={FiChevronRight} boxSize={4} />}
        >
          Fix Campaign
        </Button>
      </Box>
    </Grid>
  );
}
